/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { tokens, Icon } from '@hotjar/hotsauce';
import { useHelpHotjarUrl } from '@hotjar/translations';

import {
  ThumbsUpDownTooltip,
  ThumbsUpDownFooter,
} from '@hotjar/webapp/components/ThumbsUpDownTooltip';

const StyledButton = styled.button`
  ${tokens.transition('background-color')};
  ${tokens.elevation(0)};
  height: 32px;
  width: 32px;
  cursor: pointer;
  background: transparent;
  outline: 0;
  border: none;
  border-radius: ${tokens.radius.m};

  display: flex;
  align-content: center;
  align-items: center;
  justify-content: center;

  &[disabled] {
    opacity: 0.25;
  }
  &:focus {
    ${tokens.focus()};
  }
  &:active {
    ${tokens.elevation(-1)};
  }
  &:hover,
  &:active {
    background-color: ${tokens.color.bg_hover};
  }
`;

const StyledDiv = styled(motion.div)`
  display: flex;
  align-content: center;
  align-items: center;
`;

export const ThumbsUpDownButton = ({
  answer = null,
  onAnswerChange,
  isLoading,
  openTooltip,
  disabled,
}) => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  let icon;

  const animationWhenNotAnswered = {
    variants: {
      hide: { opacity: 0, scale: 0.5 },
      show: { opacity: 1, scale: 1 },
      highlight: { opacity: 0.83 },
    },
    initial: 'hide',
    animate: 'show',
    exit: { duration: 0 },
    whileHover: 'highlight',
  };
  const animationWhenAnswered = {
    variants: {
      hide: { opacity: 0, scale: 0.5 },
      show: { opacity: 1, scale: 1 },
      highlight: { opacity: 0.7 },
    },
    initial: 'hide',
    animate: 'show',
    exit: { duration: 0 },
    whileHover: 'highlight',
  };
  if (answer === true) {
    icon = (
      <StyledDiv key="thumbs-up" {...animationWhenAnswered}>
        <Icon key="thumbs-up" icon="thumbs_up" />
      </StyledDiv>
    );
  } else if (answer === false) {
    icon = (
      <StyledDiv key="thumbs-down" {...animationWhenAnswered}>
        <Icon key="thumbs-down" icon="thumbs_down" />
      </StyledDiv>
    );
  } else {
    icon = (
      <StyledDiv key="thumbs-up-down" {...animationWhenNotAnswered}>
        <Icon key="thumbs-up-down" icon="thumbs_up_down" />
      </StyledDiv>
    );
  }
  return (
    <ThumbsUpDownTooltip
      questionLabel={t('common:player.thumbs-up-down.body.question')}
      thumbsUpLabel={t('common:player.thumbs-up-down.body.thumbs-up-label')}
      thumbsDownLabel={t('common:player.thumbs-up-down.body.thumbs-down-label')}
      answer={answer}
      onAnswerChange={onAnswerChange}
      footer={
        <ThumbsUpDownFooter
          text={t('common:player.thumbs-up-down.footer.message')}
          href={`${helpHotjarUrl}/articles/115011647627-Recordings-Player-Guide#URL-rating`}
        />
      }
      distance={26}
      isLoading={isLoading}
      placement="bottom-end"
      callOpenTooltipOnce={openTooltip}
    >
      <StyledButton type="button" data-testid="thumbs-up-down" disabled={disabled}>
        <AnimatePresence initial={false} exitBeforeEnter>
          {icon}
        </AnimatePresence>
      </StyledButton>
    </ThumbsUpDownTooltip>
  );
};

ThumbsUpDownButton.defaultProps = {
  answer: null,
  isLoading: false,
  openTooltip: false,
  disabled: false,
};

ThumbsUpDownButton.propTypes = {
  answer: PropTypes.bool, // null is a valid value for not answered
  onAnswerChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  openTooltip: PropTypes.bool,
  disabled: PropTypes.bool,
};
